import { useEffect, useState } from "react";
import type { DetailType } from "#shared/schemas/entityDetail.ts";
import type { MbLinkEntityType } from "#shared/schemas/mbEnrich.ts";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { JsonTree } from "@web/features/settings/JsonTree.tsx";
import { MbPrettyView } from "@web/features/detail/MbPrettyView.tsx";
import { AbPrettyView } from "@web/features/detail/AbPrettyView.tsx";
import {
  useManualMbLink,
  useMbCascadeDump,
  useMbLink,
  useMbStatus,
  useStartMbEntity,
} from "@web/features/settings/mbApi.ts";
import { formatDateTime } from "@web/lib/format.ts";

function asLinkType(type: DetailType): MbLinkEntityType | null {
  if (type === "artist" || type === "track" || type === "album") return type;
  return null;
}

function mbPage(type: string, mbid: string): string {
  return `https://musicbrainz.org/${type}/${mbid}`;
}

function needsNameSearchApproval(note: string | null | undefined): boolean {
  if (!note) return false;
  return (
    note.toLowerCase().includes("confirm name search") ||
    note.toLowerCase().includes("no library track with an isrc")
  );
}

/** Cascade dump — recording + related stored entities as expandable raw JSON. */
function CascadeDump({ trackId }: { trackId: string }) {
  const dumpQ = useMbCascadeDump(trackId);
  const [openMbids, setOpenMbids] = useState<Set<string>>(new Set());

  useEffect(() => {
    // After a fresh fetch, open everything so releases / works / credits are visible.
    const entities = dumpQ.data?.entities;
    if (!entities || entities.length === 0) return;
    setOpenMbids(new Set(entities.map((e) => e.mbid)));
  }, [dumpQ.data, dumpQ.dataUpdatedAt]);

  if (dumpQ.isLoading) return <Skeleton className="h-24 w-full" />;
  const dump = dumpQ.data;
  if (!dump || dump.entities.length === 0) {
    return (
      <p className="text-xs text-muted">
        No cascade data stored yet — Enrich &amp; inspect to fetch and dump raw JSON.
      </p>
    );
  }

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">
          Cascade dump ({dump.entities.length})
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs text-accent hover:underline"
            onClick={() => setOpenMbids(new Set(dump.entities.map((e) => e.mbid)))}
          >
            Expand all
          </button>
          <button
            type="button"
            className="text-xs text-muted hover:underline"
            onClick={() => setOpenMbids(new Set())}
          >
            Collapse
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {dump.entities.map((e) => {
          const open = openMbids.has(e.mbid);
          return (
            <div key={e.mbid} className="rounded-md border border-line bg-panel">
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left"
                onClick={() =>
                  setOpenMbids((prev) => {
                    const next = new Set(prev);
                    if (next.has(e.mbid)) next.delete(e.mbid);
                    else next.add(e.mbid);
                    return next;
                  })
                }
              >
                <span className="min-w-0 truncate text-sm text-text">
                  <span className="text-faint">{e.entity_type}</span>
                  {" · "}
                  {e.name ?? e.mbid.slice(0, 8)}
                </span>
                <span className="shrink-0 text-xs text-muted">
                  {e.fetch_status}
                  {open ? " · Hide" : " · Show"}
                </span>
              </button>
              {open && (
                <div className="max-h-[28rem] space-y-2 overflow-auto border-t border-line px-3 py-2">
                  <a
                    href={mbPage(e.entity_type, e.mbid)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-accent hover:underline"
                  >
                    {e.mbid}
                  </a>
                  {e.inc_used && (
                    <p className="font-mono text-xs text-faint">inc={e.inc_used}</p>
                  )}
                  <JsonTree value={e.raw_json} name={e.entity_type} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Workbench entity inspect — raw JSON + Enrich now / Re-fetch / manual MBID. */
export function EnrichSection({
  entityType,
  entityId,
}: {
  entityType: DetailType;
  entityId: string;
}) {
  const linkType = asLinkType(entityType);
  const statusQ = useMbStatus(null, { refetchInterval: 4_000 });
  const linkQ = useMbLink(linkType, entityId);
  const start = useStartMbEntity();
  const manual = useManualMbLink();
  const [mbidDraft, setMbidDraft] = useState("");
  const [confirmName, setConfirmName] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const [dumpOpen, setDumpOpen] = useState(true);

  if (!linkType) {
    return (
      <p className="text-sm text-muted">
        MusicBrainz enrich is for artists, tracks, and albums.
      </p>
    );
  }

  if (linkQ.isLoading) return <Skeleton className="h-32 w-full" />;

  const run = statusQ.data?.run;
  const batchBusy = run?.status === "running" || run?.status === "paused";
  const thisEntityRun =
    run?.mode === "entity" &&
    run.entity_type === linkType &&
    run.entity_id === entityId;
  const entityBusy = thisEntityRun && batchBusy;
  const link = linkQ.data?.link ?? null;
  const entity = linkQ.data?.entity ?? null;
  const acousticbrainz = linkQ.data?.acousticbrainz ?? null;
  const offerNameSearch =
    linkType === "artist" && needsNameSearchApproval(link?.note) && !link?.mbid;

  const seedHint =
    linkType === "artist"
      ? "Uses a solo library track with an ISRC when possible, else any of theirs with an ISRC."
      : linkType === "album"
        ? "Uses the first library track on this album that has an ISRC."
        : "Looks up this track’s ISRC on MusicBrainz, then browses releases and cascades artists/works. Use Enrich & inspect to dump all stored JSON.";

  return (
    <div className="space-y-5">
      {batchBusy && !thisEntityRun && (
        <p className="rounded-md border border-line bg-panel px-3 py-2 text-xs text-muted">
          A batch enrich is in progress — pause or stop it first if you need this entity now.
        </p>
      )}

      {entityBusy && run && (
        <p className="rounded-md border border-line bg-panel px-3 py-2 text-xs text-muted">
          Enriching… {run.requests_made} request{run.requests_made === 1 ? "" : "s"}
          {run.current_url ? ` · ${run.current_url}` : ""}
        </p>
      )}

      {entity && link?.mbid ? (
        <section className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-semibold tracking-wider text-faint uppercase">
                MusicBrainz
              </p>
              <a
                href={mbPage(entity.entity_type, link.mbid)}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block truncate text-sm text-accent hover:underline"
              >
                {link.mbid}
              </a>
              <p className="mt-1 text-xs text-muted">
                {entity.fetch_status}
                {entity.fetched_at ? ` · ${formatDateTime(entity.fetched_at)}` : ""}
                {link.match_method ? ` · ${link.match_method}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={start.isPending || batchBusy}
                onClick={() => {
                  setDumpOpen(true);
                  start.mutate({
                    entity_type: linkType,
                    entity_id: entityId,
                    force: true,
                  });
                }}
              >
                Re-fetch
              </Button>
            </div>
          </div>
          <div className="rounded-md border border-line bg-panel px-3 py-2">
            <MbPrettyView
              raw={entity.raw_json}
              entityType={entity.entity_type}
              mbid={link.mbid}
            />
          </div>

          <div className="rounded-md border border-line bg-panel">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
              onClick={() => setRawOpen((v) => !v)}
            >
              <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                Raw JSON
              </span>
              <span className="text-xs text-muted">{rawOpen ? "Hide" : "Show"}</span>
            </button>
            {rawOpen && (
              <div className="max-h-[28rem] overflow-auto border-t border-line px-3 py-2">
                <JsonTree value={entity.raw_json} name={entity.entity_type} />
              </div>
            )}
          </div>
        </section>
      ) : null}

      {linkType === "track" && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">
              Single-track inspect
            </p>
            <Button
              size="sm"
              disabled={start.isPending || batchBusy}
              onClick={() => {
                setDumpOpen(true);
                start.mutate({
                  entity_type: "track",
                  entity_id: entityId,
                  force: true,
                });
              }}
            >
              Enrich &amp; inspect
            </Button>
          </div>
          <p className="text-xs text-muted">
            Runs the full cascade for this track only, then dumps recording + releases +
            works + credits as stored raw JSON so you can confirm data before a playlist
            or overnight run.
          </p>
          {thisEntityRun && run?.summary && !entityBusy && (
            <p className="text-xs text-muted">
              Last run: {run.summary.requests_made} requests ·{" "}
              {run.summary.by_type
                .map((r) => `${r.new_count} ${r.entity_type}`)
                .join(", ") || "nothing new"}
            </p>
          )}
          <div className="rounded-md border border-line bg-panel">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
              onClick={() => setDumpOpen((v) => !v)}
            >
              <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                Stored cascade JSON
              </span>
              <span className="text-xs text-muted">{dumpOpen ? "Hide" : "Show"}</span>
            </button>
            {dumpOpen && (
              <div className="border-t border-line px-3 py-3">
                <CascadeDump trackId={entityId} />
              </div>
            )}
          </div>
        </section>
      )}

      {linkType === "track" && acousticbrainz ? (
        <AbPrettyView entity={acousticbrainz} />
      ) : linkType === "track" && link?.mbid && !acousticbrainz ? (
        <p className="text-xs text-muted">
          AcousticBrainz not fetched yet — Re-fetch this track or run Enrich in Workbench.
        </p>
      ) : null}

      {!(entity && link?.mbid) &&
        (link && !link.locked && link.match_method === "fuzzy_name" ? (
          <section className="space-y-3">
            <p className="text-sm text-muted">
              {link.note ?? "Ambiguous name match — pick or paste an MBID."}
            </p>
            {link.candidates && link.candidates.length > 0 && (
              <ul className="space-y-2">
                {link.candidates.map((c) => (
                  <li
                    key={c.mbid}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-panel px-3 py-2"
                  >
                    <div className="min-w-0">
                      <a
                        href={mbPage("artist", c.mbid)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-accent hover:underline"
                      >
                        {c.name}
                      </a>
                      <p className="text-xs text-muted">
                        score {Math.round(c.score)}
                        {c.disambiguation ? ` · ${c.disambiguation}` : ""}
                        {c.country ? ` · ${c.country}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={manual.isPending}
                      onClick={() =>
                        manual.mutate({
                          entityType: linkType,
                          entityId,
                          mbid: c.mbid,
                        })
                      }
                    >
                      Lock
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <ManualMbid
              draft={mbidDraft}
              onChange={setMbidDraft}
              pending={manual.isPending}
              onSubmit={() =>
                manual.mutate({
                  entityType: linkType,
                  entityId,
                  mbid: mbidDraft.trim(),
                })
              }
            />
          </section>
        ) : (
          <section className="space-y-3">
            <p className="text-sm text-muted">{link?.note ?? "Not yet enriched."}</p>
            <p className="text-xs text-faint">{seedHint}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={start.isPending || batchBusy}
                onClick={() => {
                  if (linkType === "track") setDumpOpen(true);
                  start.mutate({
                    entity_type: linkType,
                    entity_id: entityId,
                    force: true,
                  });
                }}
              >
                {linkType === "track" ? "Enrich & inspect" : "Enrich now"}
              </Button>
              {offerNameSearch && (
                <Button
                  variant="secondary"
                  disabled={start.isPending || batchBusy}
                  onClick={() => setConfirmName(true)}
                >
                  Search by name…
                </Button>
              )}
            </div>

            {confirmName && (
              <div className="space-y-2 rounded-md border border-line bg-panel px-3 py-3">
                <p className="text-sm text-text">
                  No ISRC seed available. Run a MusicBrainz{" "}
                  <strong>artist name search</strong>? This can be ambiguous — you’ll still
                  review fuzzy matches.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={start.isPending || batchBusy}
                    onClick={() => {
                      setConfirmName(false);
                      start.mutate({
                        entity_type: "artist",
                        entity_id: entityId,
                        force: true,
                        allow_name_search: true,
                      });
                    }}
                  >
                    Yes, search by name
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmName(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <ManualMbid
              draft={mbidDraft}
              onChange={setMbidDraft}
              pending={manual.isPending}
              onSubmit={() =>
                manual.mutate({
                  entityType: linkType,
                  entityId,
                  mbid: mbidDraft.trim(),
                })
              }
            />
          </section>
        ))}
    </div>
  );
}

function ManualMbid({
  draft,
  onChange,
  pending,
  onSubmit,
}: {
  draft: string;
  onChange: (v: string) => void;
  pending: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted">Or paste an MBID manually</p>
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="min-w-[14rem] flex-1 rounded-md border border-line bg-panel px-3 py-2 font-mono text-xs text-text outline-none focus:border-accent"
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={pending || draft.trim().length < 36}
          onClick={onSubmit}
        >
          Lock MBID
        </Button>
      </div>
    </div>
  );
}
